import * as React from "react";
import { TableHead, TableRow, TableCell, TableBody } from "../../../components/mui";

/**
 * Generic monthly summary table shell used by Rental, Misc Expense,
 * Utilities, and Allowance tabs.
 *
 * Props:
 * - columns: [{ id, label, className?, renderCell?(row) }]
 * - rows: array of row objects
 * - showMonthlyPayments: boolean
 * - monthKeys: ordered list of month field names on the row
 * - renderMonthlyCell?(row, monthKey): ReactNode (defaults to row[monthKey])
 * - annualHeaders?: [{ id, label }]
 * - annualCellsRenderer?(row): ReactNode[] (e.g. Annual Paid/Unpaid/Total cells)
 * - renderTotalsRow?(): ReactNode (optional yellow totals row)
 */
export default function MonthlySummaryTable({
  columns,
  rows,
  showMonthlyPayments,
  monthKeys,
  renderMonthlyCell,
  annualHeaders,
  annualCellsRenderer,
  renderTotalsRow
}) {
  const safeRenderMonthlyCell = React.useCallback(
    (row, key) => {
      if (renderMonthlyCell) {
        return renderMonthlyCell(row, key);
      }
      return row[key];
    },
    [renderMonthlyCell]
  );

  const hasAnnual = Array.isArray(annualHeaders) && annualHeaders.length > 0;

  return (
    <>
      <TableHead>
        <TableRow>
          {columns.map((column) => (
            <TableCell key={column.id}>{column.label}</TableCell>
          ))}
          {showMonthlyPayments &&
            monthKeys.map((key) => (
              <TableCell key={key}>{toMonthLabel(key)}</TableCell>
            ))}
          {hasAnnual &&
            annualHeaders.map((header) => (
              <TableCell key={header.id}>{header.label}</TableCell>
            ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow hover key={row.id}>
            {columns.map((column) => (
              <TableCell key={column.id} className={column.className}>
                {column.renderCell ? column.renderCell(row) : row[column.id]}
              </TableCell>
            ))}
            {showMonthlyPayments &&
              monthKeys.map((key) => (
                <TableCell key={key} className="payment-summary-number-cell">
                  {safeRenderMonthlyCell(row, key)}
                </TableCell>
              ))}
            {hasAnnual &&
              annualCellsRenderer &&
              annualCellsRenderer(row)?.map((cell, index) => (
                <React.Fragment key={`annual-${row.id}-${index}`}>{cell}</React.Fragment>
              ))}
          </TableRow>
        ))}

        {renderTotalsRow && renderTotalsRow()}
      </TableBody>
    </>
  );
}

function toMonthLabel(key) {
  switch (key) {
    case "january":
      return "January";
    case "february":
      return "February";
    case "march":
      return "March";
    case "april":
      return "April";
    case "may":
      return "May";
    case "june":
      return "June";
    case "july":
      return "July";
    case "august":
      return "August";
    case "september":
      return "September";
    case "october":
      return "October";
    case "november":
      return "November";
    case "december":
      return "December";
    default:
      return key;
  }
}
